import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import api from '../api/client'
import { NewProductSEO } from '../hooks/useSEO'
import { productCategories, productPlatforms } from '../types'

export default function NewProductPage() {
  const navigate = useNavigate()
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    content: '',
    price: 100,
    category: 'SKILL' as const,
    platform: 'GENERIC' as const,
    tags: '',
    isUnlimited: true,
    stockQuantity: 10,
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isLoading, setIsLoading] = useState(false)
  const [serverError, setServerError] = useState('')

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    setErrors((prev) => ({ ...prev, [name]: '' }))
  }

  const validate = () => {
    const newErrors: Record<string, string> = {}
    if (formData.title.length < 5) newErrors.title = 'Title must be at least 5 characters'
    if (formData.title.length > 100) newErrors.title = 'Title must be at most 100 characters'
    if (formData.description.length < 20) newErrors.description = 'Description must be at least 20 characters'
    if (formData.description.length > 500) newErrors.description = 'Description must be at most 500 characters'
    if (formData.content.length < 100) newErrors.content = 'Content must be at least 100 characters'
    if (formData.content.length > 50000) newErrors.content = 'Content must be at most 50,000 characters'
    if (formData.price < 1) newErrors.price = 'Minimum price is 1 USDC'
    if (formData.price > 100000) newErrors.price = 'Maximum price is 100,000 USDC'
    if (!formData.isUnlimited && formData.stockQuantity < 1) newErrors.stockQuantity = 'Stock quantity must be at least 1'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return

    setIsLoading(true)
    setServerError('')
    try {
      const tags = formData.tags
        .split(',')
        .map((t) => t.trim())
        .filter((t) => t.length > 0)
        .slice(0, 9)

      await api.post('/products', {
        title: formData.title,
        description: formData.description,
        content: formData.content,
        price: Number(formData.price),
        category: formData.category,
        platform: formData.platform,
        stockQuantity: formData.isUnlimited ? null : Number(formData.stockQuantity),
        tags,
      })

      navigate('/my-products')
    } catch (err) {
      setServerError(err instanceof Error ? err.message : 'Failed to create product')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div>
      <NewProductSEO />
      <div style={{ marginBottom: '1.5rem' }}>
        <Link to="/products" className="text-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back to Marketplace
        </Link>
      </div>

      <div className="card-static" style={{ maxWidth: 700 }}>
        <h1 style={{ marginBottom: '1.5rem' }}>List a New Product</h1>
        <p className="text-secondary" style={{ marginBottom: '1.5rem' }}>
          Create a product listing for skills, prompts, configurations, or workflows. Products are available for sale immediately.
        </p>

        {serverError && <div className="alert alert-error">{serverError}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="label" htmlFor="title">Title</label>
            <input
              type="text"
              id="title"
              name="title"
              className={`input ${errors.title ? 'input-error' : ''}`}
              value={formData.title}
              onChange={handleChange}
              placeholder="e.g., Advanced Code Review Skill for Claude"
              disabled={isLoading}
            />
            {errors.title && <p className="form-error">{errors.title}</p>}
          </div>

          <div className="form-group">
            <label className="label" htmlFor="description">Short Description</label>
            <textarea
              id="description"
              name="description"
              className={`input textarea ${errors.description ? 'input-error' : ''}`}
              value={formData.description}
              onChange={handleChange}
              placeholder="Brief description of what this product does (20-500 characters)"
              style={{ minHeight: 80 }}
              disabled={isLoading}
            />
            <p className="form-hint">{formData.description.length}/500 characters</p>
            {errors.description && <p className="form-error">{errors.description}</p>}
          </div>

          <div className="form-group">
            <label className="label" htmlFor="content">Product Content</label>
            <textarea
              id="content"
              name="content"
              className={`input textarea ${errors.content ? 'input-error' : ''}`}
              value={formData.content}
              onChange={handleChange}
              placeholder="The full content of your product (skill.md, prompt template, configuration, etc.)"
              style={{ minHeight: 300, fontFamily: 'monospace', fontSize: '14px' }}
              disabled={isLoading}
            />
            <p className="form-hint">{formData.content.length.toLocaleString()}/50,000 characters</p>
            {errors.content && <p className="form-error">{errors.content}</p>}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="form-group">
              <label className="label" htmlFor="category">Category</label>
              <select
                id="category"
                name="category"
                className="input select"
                value={formData.category}
                onChange={handleChange}
                disabled={isLoading}
              >
                {productCategories.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="label" htmlFor="platform">Target Platform</label>
              <select
                id="platform"
                name="platform"
                className="input select"
                value={formData.platform}
                onChange={handleChange}
                disabled={isLoading}
              >
                {productPlatforms.map((plat) => (
                  <option key={plat.value} value={plat.value}>
                    {plat.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="label" htmlFor="price">Price (USDC)</label>
            <input
              type="number"
              id="price"
              name="price"
              className={`input ${errors.price ? 'input-error' : ''}`}
              value={formData.price}
              onChange={handleChange}
              min={1}
              max={100000}
              disabled={isLoading}
            />
            {errors.price && <p className="form-error">{errors.price}</p>}
            <p className="form-hint">You will receive 90.1% of each sale (9.9% platform fee)</p>
          </div>

          <div className="form-group">
            <label className="label">Stock Quantity</label>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '0.5rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="stockType"
                  checked={formData.isUnlimited}
                  onChange={() => setFormData((prev) => ({ ...prev, isUnlimited: true }))}
                  disabled={isLoading}
                />
                Unlimited (digital product)
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="stockType"
                  checked={!formData.isUnlimited}
                  onChange={() => setFormData((prev) => ({ ...prev, isUnlimited: false }))}
                  disabled={isLoading}
                />
                Limited quantity
              </label>
            </div>
            {!formData.isUnlimited && (
              <>
                <input
                  type="number"
                  id="stockQuantity"
                  name="stockQuantity"
                  className={`input ${errors.stockQuantity ? 'input-error' : ''}`}
                  value={formData.stockQuantity}
                  onChange={handleChange}
                  min={1}
                  disabled={isLoading}
                  style={{ maxWidth: 150 }}
                />
                {errors.stockQuantity && <p className="form-error">{errors.stockQuantity}</p>}
              </>
            )}
            <p className="form-hint">
              {formData.isUnlimited
                ? 'Product can be purchased unlimited times'
                : `Product can be purchased ${formData.stockQuantity} time(s)`}
            </p>
          </div>

          <div className="form-group">
            <label className="label" htmlFor="tags">Tags (comma separated, max 9)</label>
            <input
              type="text"
              id="tags"
              name="tags"
              className="input"
              value={formData.tags}
              onChange={handleChange}
              placeholder="e.g., code-review, typescript, automation"
              disabled={isLoading}
            />
          </div>

          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <button type="submit" className="btn btn-primary" disabled={isLoading}>
              {isLoading ? 'Creating...' : 'List Product'}
            </button>
            <p className="text-secondary" style={{ fontSize: '14px' }}>
              Your product will be available for sale immediately
            </p>
          </div>
        </form>
      </div>
    </div>
  )
}
